import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Card } from '../components/ui/card';
import { eventsService } from '../services/eventsService';
import { EventKPICards, EventFilters, LoadingSpinner } from '../components/events';
import { StatesDistrictsModal } from '../components/events/StatesDistrictsModal';
import { toISTISOString } from '../lib/utils';

const EventList: React.FC = () => {
    const [totalEvents, setTotalEvents] = useState<number>(0);
    const [totalMusterRolls, setTotalMusterRolls] = useState<number>(0);
    const [filteredEventsCount, setFilteredEventsCount] = useState<number>(0);
    const [filteredMusterRolls, setFilteredMusterRolls] = useState<number>(0);
    const [totalStates, setTotalStates] = useState<number>(0);
    const [totalDistricts, setTotalDistricts] = useState<number>(0);
    const [isLoadingInitialData, setIsLoadingInitialData] = useState<boolean>(true);
    const [isStatesModalOpen, setIsStatesModalOpen] = useState<boolean>(false);

    // Filter states
    const [dateRange, setDateRange] = useState<'1h' | '24h' | '7d' | '30d' | 'custom'>('custom');
    const [eventNameFilter, setEventNameFilter] = useState<string[]>(['EKYCDataStored', 'EKYCDataUpdated', 'JobCardDataStored', 'JobCardDataUpdated', 'MateAddressSet', 'PeriodAttendanceFinalized']);
    
    // Set default dates to 1 week ago to today
    const getDefaultDates = () => {
        const today = new Date();
        const oneWeekAgo = new Date();
        oneWeekAgo.setDate(today.getDate() - 7);
        return {
            from: oneWeekAgo,
            to: today
        };
    };

    const defaultDates = getDefaultDates();
    const [customFromDate, setCustomFromDate] = useState<string>(() => {
        return toISTISOString(defaultDates.from);
    });
    const [customToDate, setCustomToDate] = useState<string>(() => {
        return toISTISOString(defaultDates.to);
    });

    // Track if this is the first mount
    const isFirstMount = useRef(true);

    // Fetch initial data (events, muster rolls, and geography counts) on mount
    useEffect(() => {
        const fetchInitialData = async () => {
            setIsLoadingInitialData(true);
            try {
                const [eventsResponse, musterRollsResponse, geographyResponse] = await Promise.all([
                    eventsService.getTotalEvents(customFromDate, customToDate),
                    eventsService.getTotalMusterRolls(customFromDate, customToDate),
                    eventsService.getGeographyCounts()
                ]);
                
                setTotalEvents(eventsResponse.totalEvents);
                setFilteredEventsCount(eventsResponse.filteredEvents);
                setTotalMusterRolls(musterRollsResponse.totalMusterRolls);
                setFilteredMusterRolls(musterRollsResponse.filteredMusterRolls);
                setTotalStates(geographyResponse.totalStates);
                setTotalDistricts(geographyResponse.totalDistricts);
            } catch (error) {
                console.error('Error fetching initial data:', error);
                setTotalEvents(0);
                setFilteredEventsCount(0);
                setTotalMusterRolls(0);
                setFilteredMusterRolls(0);
                setTotalStates(0);
                setTotalDistricts(0);
            } finally {
                setIsLoadingInitialData(false);
                isFirstMount.current = false;
            }
        };

        fetchInitialData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Fetch total events and muster rolls when date filters change (after initial load)
    useEffect(() => {
        if (isFirstMount.current) return; // Skip on first mount
        
        const fetchFilteredData = async () => {
            setIsLoadingInitialData(true);
            try {
                const [eventsResponse, musterRollsResponse] = await Promise.all([
                    eventsService.getTotalEvents(customFromDate, customToDate),
                    eventsService.getTotalMusterRolls(customFromDate, customToDate)
                ]);
                
                setTotalEvents(eventsResponse.totalEvents);
                setFilteredEventsCount(eventsResponse.filteredEvents);
                setTotalMusterRolls(musterRollsResponse.totalMusterRolls);
                setFilteredMusterRolls(musterRollsResponse.filteredMusterRolls);
            } catch (error) {
                console.error('Error fetching filtered data:', error);
                setTotalEvents(0);
                setFilteredEventsCount(0);
                setTotalMusterRolls(0);
                setFilteredMusterRolls(0);
            } finally {
                setIsLoadingInitialData(false);
            }
        };

        fetchFilteredData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [customFromDate, customToDate]);


    const handleCustomDateChange = (fromDate: string, toDate: string) => {
        setCustomFromDate(fromDate);
        setCustomToDate(toDate);
    };

    const [isApplyingFilters, setIsApplyingFilters] = useState(false);

    const handleApplyFilters = async () => {
        // Filters are already applied via the onChange handlers
        setIsApplyingFilters(true);
        setIsLoadingInitialData(true);
        
        // Force refresh of the data
        try {
            const [eventsResponse, musterRollsResponse] = await Promise.all([
                eventsService.getTotalEvents(customFromDate, customToDate),
                eventsService.getTotalMusterRolls(customFromDate, customToDate)
            ]);
            
            setTotalEvents(eventsResponse.totalEvents);
            setFilteredEventsCount(eventsResponse.filteredEvents);
            setTotalMusterRolls(musterRollsResponse.totalMusterRolls);
            setFilteredMusterRolls(musterRollsResponse.filteredMusterRolls);
        } catch (error) {
            console.error('Error applying filters:', error);
        } finally {
            setIsApplyingFilters(false);
            setIsLoadingInitialData(false);
        }
    };

    const handleClearFilters = () => {
        // Filters are already cleared via the onChange handlers
        // This callback can be used for any additional logic if needed
    };

    return (
        <div className="space-y-6">
            {/* Analytics Cards Section */}

            {/* Filters Section */}
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }}>
                <Card className="p-4 sm:p-4">
                    <EventFilters dateRange={dateRange} eventNameInput={eventNameFilter} onDateRangeChange={setDateRange} onEventNameChange={setEventNameFilter} onCustomDateChange={handleCustomDateChange} onApplyFilters={handleApplyFilters} onClearFilters={handleClearFilters} initialFromDate={customFromDate} initialToDate={customToDate} isLoading={isApplyingFilters} />
                </Card>
            </motion.div>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                <Card className="p-4 sm:p-4">
                    {isLoadingInitialData ? (
                        <LoadingSpinner />
                    ) : (
                        <EventKPICards totalEvents={totalEvents} filteredEvents={filteredEventsCount} totalMusterRolls={totalMusterRolls} filteredMusterRolls={filteredMusterRolls} totalStates={totalStates} totalDistricts={totalDistricts} onStatesCardClick={() => setIsStatesModalOpen(true)} />
                    )}
                </Card>
            </motion.div>
            <StatesDistrictsModal open={isStatesModalOpen} onOpenChange={setIsStatesModalOpen} />
        </div>
    );
};

export default EventList;
